import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useDarkMode } from "@/App";
import { toast } from "sonner";
import { Users, Crown, RefreshCw, Menu, X, Trophy, Moon, Sun } from "lucide-react";
import LiveMatchCard from "@/components/LiveMatchCard";
import AuthModal from "@/components/AuthModal";
import SubscriptionModal from "@/components/SubscriptionModal";
import AdSpace, { SidebarAd, InlineAd, BannerAd } from "@/components/AdSpace";
import Footer from "@/components/Footer";

const SPORT_IMAGES = {
  ipl: "/sports-icons/ipl.png",
  all: null,
  cricket: "/sports-icons/cricket.png",
  football: "/sports-icons/football.png",
  hockey: "/sports-icons/hockey.png",
  badminton: "/sports-icons/badminton.png",
  tennis: "/sports-icons/tennis.png",
  kabaddi: "/sports-icons/kabaddi.png",
  athletics: "/sports-icons/athletics.png",
  basketball: "/sports-icons/basketball.png",
};

const SPORTS = [
  { key: "all", label: "All", color: "#00bf63" },
  { key: "cricket", label: "Cricket", color: "#22c55e" },
  { key: "football", label: "Football", color: "#3b82f6" },
  { key: "hockey", label: "Hockey", color: "#f97316" },
  { key: "badminton", label: "Badminton", color: "#a855f7" },
  { key: "tennis", label: "Tennis", color: "#eab308" },
  { key: "kabaddi", label: "Kabaddi", color: "#ef4444" },
  { key: "athletics", label: "Athletics", color: "#ec4899" },
  { key: "basketball", label: "Basketball", color: "#f59e0b" },
];

const Home = () => {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sportsMenuOpen, setSportsMenuOpen] = useState(false);
  const [activeSport, setActiveSport] = useState("all");
  const [apiMessage, setApiMessage] = useState(null);
  const [showIPL, setShowIPL] = useState(false);
  const [iplPoints, setIplPoints] = useState([]);
  const [iplFixtures, setIplFixtures] = useState([]);
  const [iplLoading, setIplLoading] = useState(false);
  const [iplTab, setIplTab] = useState("fixtures");

  useEffect(() => { checkAuth(); }, []);
  useEffect(() => { if (!showIPL) fetchMatches(activeSport); }, [activeSport]);
  useEffect(() => {
    if (showIPL) return;
    const iv = setInterval(() => fetchMatches(activeSport, true), 30000);
    return () => clearInterval(iv);
  }, [activeSport, showIPL]);
  useEffect(() => {
    if (!showIPL) return;
    const iv = setInterval(() => { fetchIPLFixtures(); fetchIPLPoints(); }, 30000);
    return () => clearInterval(iv);
  }, [showIPL]);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const u = await axios.get(`${API}/users/me`);
      setUser(u.data);
      const s = await axios.get(`${API}/subscriptions/me`);
      setSubscription(s.data);
    } catch { localStorage.removeItem("token"); }
  };

  const fetchMatches = async (sport, silent = false) => {
    if (!silent) setLoading(true);
    setApiMessage(null);
    try {
      const r = await axios.get(`${API}/sports/${sport}`);
      if (r.data.status === "success") {
        setMatches(r.data.data || []);
        if (r.data.message) setApiMessage(r.data.message);
      } else if (!silent) setMatches([]);
    } catch (e) {
      if (!silent) { toast.error("Failed to load"); setMatches([]); }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleLogout = () => { localStorage.removeItem("token"); setUser(null); setSubscription(null); toast.success("Logged out"); };
  const fetchIPLPoints = async () => { try { const r = await axios.get(`${API}/cricket/ipl/points`); if (r.data.status === "success") setIplPoints(r.data.data || []); } catch {} };
  const fetchIPLFixtures = async () => { try { const r = await axios.get(`${API}/cricket/ipl/schedule`); if (r.data.status === "success") setIplFixtures(r.data.data || []); } catch {} };

  const handleIPLClick = () => {
    const next = !showIPL;
    setShowIPL(next);
    if (next) { setIplLoading(true); Promise.all([fetchIPLFixtures(), fetchIPLPoints()]).finally(() => setIplLoading(false)); }
  };

  const handleSportClick = (sport) => { setShowIPL(false); setActiveSport(sport); };
  const handleAuthSuccess = (u) => { setUser(u); setShowAuthModal(false); checkAuth(); };
  const isPremium = subscription?.subscription_type === "premium";

  // Group matches: live → recent scored → upcoming → rest
  const grouped = (() => {
    const now = new Date();
    const week = new Date(now.getTime() + 7 * 86400000);
    const todayStr = now.toISOString().split("T")[0];
    const weekStr = week.toISOString().split("T")[0];
    const live = [], scored = [], upcoming = [], rest = [];
    for (const m of matches) {
      const d = m.date || m.dateTimeGMT?.split("T")[0] || "";
      const hasScore = (m.score?.length > 0) || (m.homeScore != null && String(m.homeScore) !== "None");
      if (m.status === "live") live.push(m);
      else if (m.status === "completed" && hasScore) scored.push(m);
      else if (m.status === "upcoming" && d >= todayStr && d <= weekStr) upcoming.push(m);
      else rest.push(m);
    }
    upcoming.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    scored.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return { live, scored, upcoming, rest };
  })();

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      {/* Header */}
      <header className="backdrop-blur-xl border-b sticky top-0 z-50" style={{ background: "var(--header-bg)", borderColor: "var(--header-border)" }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-6">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={() => setSportsMenuOpen(!sportsMenuOpen)} className="p-2 rounded-full transition-colors hover:bg-[#00bf63]/10" style={{ color: "var(--text-secondary)" }} data-testid="hamburger-menu-btn" title="Sports Menu">
                {sportsMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <span className="text-lg sm:text-xl font-semibold text-[#00bf63] tracking-tight cursor-pointer" onClick={() => { setShowIPL(false); setActiveSport("all"); setSportsMenuOpen(false); }}>ScoreXprt</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <button onClick={() => { if (showIPL) { fetchIPLFixtures(); fetchIPLPoints(); } else fetchMatches(activeSport); toast.success("Refreshed"); }} className="p-1.5 sm:p-2 rounded-full transition-colors hover:bg-[#00bf63]/10" style={{ color: "var(--text-secondary)" }} data-testid="refresh-btn" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
              <button onClick={toggleDarkMode} className="p-1.5 sm:p-2 rounded-full transition-colors" style={{ color: "var(--text-secondary)" }} data-testid="dark-mode-toggle" title={darkMode ? "Light" : "Dark"}>
                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              {user ? (
                <>
                  <div className="hidden sm:flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}><Users className="w-4 h-4" /><span>{user.username}</span>{isPremium && <Crown className="w-4 h-4 text-amber-500" />}</div>
                  {!isPremium && <button onClick={() => setShowSubscriptionModal(true)} className="hidden sm:block text-sm font-medium text-amber-500" data-testid="upgrade-btn">Premium</button>}
                  <button onClick={handleLogout} className="hidden sm:block text-sm" style={{ color: "var(--text-muted)" }} data-testid="logout-btn">Sign Out</button>
                  <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="sm:hidden p-2" data-testid="mobile-menu-btn">{mobileMenuOpen ? <X className="w-5 h-5" style={{ color: "var(--text-primary)" }} /> : <Menu className="w-5 h-5" style={{ color: "var(--text-primary)" }} />}</button>
                </>
              ) : (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <button onClick={() => setShowAuthModal(true)} className="text-xs sm:text-sm font-medium whitespace-nowrap" style={{ color: "var(--text-secondary)" }} data-testid="login-btn">Sign In</button>
                  <button onClick={() => setShowAuthModal(true)} className="text-xs sm:text-sm font-medium text-white bg-[#00bf63] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-[#00a854] whitespace-nowrap" data-testid="register-btn">Register</button>
                </div>
              )}
            </div>
          </div>
          {user && mobileMenuOpen && (
            <div className="sm:hidden py-3 space-y-2" style={{ borderTop: "1px solid var(--border-color)" }}>
              <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}><Users className="w-4 h-4" /><span>{user.username}</span></div>
              {!isPremium && <button onClick={() => { setShowSubscriptionModal(true); setMobileMenuOpen(false); }} className="block text-sm font-medium text-amber-500">Premium</button>}
              <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block text-sm" style={{ color: "var(--text-muted)" }}>Sign Out</button>
            </div>
          )}
        </div>
      </header>

      {/* Sports Side Menu */}
      {sportsMenuOpen && <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setSportsMenuOpen(false)} />}
      <div className={`fixed top-0 left-0 h-full w-72 z-50 transform transition-transform duration-300 ease-in-out ${sportsMenuOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ background: "var(--bg-primary)", borderRight: "1px solid var(--border-color)" }} data-testid="sports-side-menu">
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--border-color)" }}>
          <span className="text-lg font-bold text-[#00bf63]">Sports</span>
          <button onClick={() => setSportsMenuOpen(false)} className="p-1 rounded-full hover:bg-[#00bf63]/10"><X className="w-5 h-5" style={{ color: "var(--text-secondary)" }} /></button>
        </div>
        <nav className="py-2 overflow-y-auto" style={{ maxHeight: "calc(100vh - 60px)" }}>
          {/* IPL */}
          <button onClick={() => { handleIPLClick(); setSportsMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-5 py-3 transition-colors ${showIPL ? "bg-[#1a237e]/10" : "hover:bg-[#00bf63]/5"}`}
            data-testid="menu-ipl-btn">
            <img src="/sports-icons/ipl.png" alt="IPL" className="w-7 h-7 object-contain" />
            <span className="text-sm font-semibold" style={{ color: showIPL ? "#1a237e" : "var(--text-primary)" }}>IPL 2026</span>
            {showIPL && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#1a237e]" />}
          </button>
          {/* All Sports */}
          {SPORTS.map(s => {
            const imgSrc = SPORT_IMAGES[s.key];
            const isActive = activeSport === s.key && !showIPL;
            return (
              <button key={s.key} onClick={() => { handleSportClick(s.key); setSportsMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-5 py-3 transition-colors ${isActive ? "bg-[#00bf63]/10" : "hover:bg-[#00bf63]/5"}`}
                data-testid={`menu-sport-${s.key}`}>
                {imgSrc ? (
                  <img src={imgSrc} alt={s.label} className="w-7 h-7 object-contain" />
                ) : (
                  <Trophy className="w-6 h-6" style={{ color: s.color }} />
                )}
                <span className="text-sm font-semibold" style={{ color: isActive ? "#00bf63" : "var(--text-primary)" }}>{s.label}</span>
                {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#00bf63]" />}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Hero */}
      <div className="bg-[#00bf63] pt-8 sm:pt-14 pb-8 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-semibold text-white tracking-tight">Live Scores for all Indian Matches</h1>
        </div>
      </div>

      {/* Sports Grid */}
      <div className="max-w-6xl mx-auto px-3 sm:px-6 -mt-5 sm:-mt-6 relative z-10">
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5 sm:gap-2" data-testid="sports-grid">
          {/* IPL Special */}
          <button onClick={handleIPLClick} className={`flex flex-col items-center gap-1 sm:gap-1.5 p-2 sm:p-3 rounded-xl transition-all ${showIPL ? "ring-2 ring-[#1a237e] shadow-lg" : "hover:shadow-md"}`}
            style={{ background: showIPL ? "#1a237e" : "var(--bg-card)", border: "1px solid var(--border-color)" }} data-testid="filter-ipl-btn">
            <img src="/sports-icons/ipl.png" alt="IPL" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
            <span className="text-[10px] sm:text-[11px] font-semibold" style={{ color: showIPL ? "#fff" : "var(--text-primary)" }}>IPL</span>
          </button>
          {SPORTS.map(s => {
              const imgSrc = SPORT_IMAGES[s.key];
              const isActive = activeSport === s.key && !showIPL;
              return (
                <button key={s.key} onClick={() => handleSportClick(s.key)}
                  className={`flex flex-col items-center gap-1 sm:gap-1.5 p-2 sm:p-3 rounded-xl transition-all ${isActive ? "ring-2 shadow-lg" : "hover:shadow-md"}`}
                  style={{
                    background: isActive ? s.color : "var(--bg-card)",
                    border: "1px solid var(--border-color)",
                    ...(isActive ? { ringColor: s.color } : {}),
                  }}
                  data-testid={`sport-tab-${s.key}`}>
                  {imgSrc ? (
                    <img src={imgSrc} alt={s.label} className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
                  ) : (
                    <Trophy className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: isActive ? "#fff" : s.color }} />
                  )}
                  <span className="text-[10px] sm:text-[11px] font-semibold" style={{ color: isActive ? "#fff" : "var(--text-primary)" }}>{s.label}</span>
                </button>
              );
            })}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <div className="flex gap-4 sm:gap-8">
          <div className="flex-1 min-w-0">
            {showIPL ? (
              <IPLSection iplFixtures={iplFixtures} iplPoints={iplPoints} iplLoading={iplLoading}
                iplTab={iplTab} setIplTab={setIplTab} isPremium={isPremium} navigate={navigate}
                setShowSubscriptionModal={setShowSubscriptionModal} />
            ) : loading ? (
              <div className="flex justify-center items-center min-h-[200px]" data-testid="loading-spinner">
                <div className="w-8 h-8 border-2 border-[#00bf63]/20 border-t-[#00bf63] rounded-full animate-spin" />
              </div>
            ) : apiMessage ? (
              <ComingSoon sport={activeSport} message={apiMessage} />
            ) : matches.length === 0 ? (
              <div className="text-center py-16 rounded-2xl" style={{ background: "var(--bg-card)" }} data-testid="no-matches">
                <Trophy className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
                <h3 className="text-lg font-semibold mb-1" style={{ color: "var(--text-primary)" }}>No matches found</h3>
                <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No live or recent Indian {activeSport} matches right now</p>
              </div>
            ) : (
              <div className="space-y-6" data-testid="matches-container">
                {grouped.live.length > 0 && (
                  <Section title="Live Now" count={grouped.live.length} dotColor="bg-red-500" pulse testId="live-section" isPremium={isPremium} onUpgradeClick={() => setShowSubscriptionModal(true)}>
                    {grouped.live.map((m, i) => (
                      <div key={m.id || i} className="relative">
                        <div className="absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-red-500 via-orange-500 to-red-500 animate-pulse opacity-60" />
                        <div className="relative"><LiveMatchCard match={m} /></div>
                      </div>
                    ))}
                  </Section>
                )}
                {grouped.scored.length > 0 && (
                  <Section title="Recent Scores" count={grouped.scored.length} dotColor="bg-[#00bf63]" testId="recent-scores-section" isPremium={isPremium} onUpgradeClick={() => setShowSubscriptionModal(true)}>
                    {grouped.scored.map((m, i) => <LiveMatchCard key={m.id || i} match={m} />)}
                  </Section>
                )}
                {!isPremium && (grouped.live.length + grouped.scored.length) > 0 && <BannerAd />}
                {grouped.upcoming.length > 0 && (
                  <Section title="Upcoming This Week" count={grouped.upcoming.length} dotColor="bg-blue-500" testId="upcoming-section" isPremium={isPremium} onUpgradeClick={() => setShowSubscriptionModal(true)}>
                    {grouped.upcoming.map((m, i) => <LiveMatchCard key={m.id || i} match={m} />)}
                  </Section>
                )}
                {grouped.rest.length > 0 && (
                  <Section title="Other" count={grouped.rest.length} dotColor="bg-gray-400" testId="completed-section" isPremium={isPremium} onUpgradeClick={() => setShowSubscriptionModal(true)}>
                    {grouped.rest.map((m, i) => <LiveMatchCard key={m.id || i} match={m} />)}
                  </Section>
                )}
                {!isPremium && <BannerAd />}
              </div>
            )}
          </div>
          {!isPremium && (
            <div className="hidden lg:block w-[280px] flex-shrink-0">
              <div className="sticky top-20 space-y-4">
                <SidebarAd size="medium" />
                <div className="rounded-2xl p-5" style={{ background: "var(--bg-card)", border: "1px solid var(--border-color)" }}>
                  <div className="flex items-center gap-2 mb-2"><Crown className="w-5 h-5 text-[#00bf63]" /><span className="font-semibold" style={{ color: "var(--text-primary)" }}>Go Premium</span></div>
                  <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>Remove ads for uninterrupted scores.</p>
                  <button onClick={() => setShowSubscriptionModal(true)} className="w-full py-2 bg-[#00bf63] text-white text-sm font-medium rounded-full hover:bg-[#00a854]" data-testid="sidebar-upgrade-btn">Upgrade</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer onUpgradeClick={() => setShowSubscriptionModal(true)} />
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} />}
      {showSubscriptionModal && user && <SubscriptionModal onClose={() => setShowSubscriptionModal(false)} onSuccess={() => { setShowSubscriptionModal(false); checkAuth(); }} currentSubscription={subscription} />}
    </div>
  );
};

// Section wrapper with inline ad after 2nd card
const Section = ({ title, count, dotColor, pulse, testId, children, isPremium, onUpgradeClick }) => {
  const items = React.Children.toArray(children);
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${dotColor} ${pulse ? "animate-pulse" : ""}`} />
        <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{title} ({count})</h2>
      </div>
      <div className="space-y-3">
        {items.map((child, i) => (
          <React.Fragment key={i}>
            {child}
            {i === 1 && items.length > 2 && !isPremium && <InlineAd onUpgradeClick={onUpgradeClick} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// IPL Section
const IPLSection = ({ iplFixtures, iplPoints, iplLoading, iplTab, setIplTab, isPremium, navigate, setShowSubscriptionModal }) => (
  <div className="space-y-5" data-testid="ipl-content">
    <div className="bg-gradient-to-r from-[#1a237e] to-[#283593] rounded-2xl p-4 sm:p-6 text-white">
      <div className="flex items-center gap-2 sm:gap-3 mb-1"><Trophy className="w-5 h-5 sm:w-7 sm:h-7" /><h2 className="text-lg sm:text-2xl font-bold">TATA IPL 2026</h2></div>
      <p className="text-white/60 text-xs sm:text-sm">Indian Premier League</p>
    </div>
    <div className="flex gap-2">
      <button onClick={() => setIplTab("fixtures")} className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all ${iplTab === "fixtures" ? "bg-[#1a237e] text-white shadow-md" : ""}`}
        style={iplTab !== "fixtures" ? { background: "var(--bg-card-inner)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" } : {}}>
        Fixtures & Results ({iplFixtures.length})
      </button>
      <button onClick={() => setIplTab("points")} className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all ${iplTab === "points" ? "bg-[#1a237e] text-white shadow-md" : ""}`}
        style={iplTab !== "points" ? { background: "var(--bg-card-inner)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" } : {}}>
        Points Table
      </button>
    </div>
    {iplLoading ? (
      <div className="flex justify-center items-center min-h-[200px]"><div className="w-8 h-8 border-2 border-[#1a237e]/20 border-t-[#1a237e] rounded-full animate-spin" /></div>
    ) : iplTab === "fixtures" ? (
      <div className="space-y-3" data-testid="ipl-fixtures">
        {iplFixtures.map((f, idx) => (
          <React.Fragment key={f.id || idx}>
            <div className="rounded-2xl shadow-sm p-3 sm:p-5 hover:shadow-md transition-all cursor-pointer overflow-hidden"
              style={{ background: "var(--bg-card)", border: "1px solid var(--border-color)" }}
              onClick={() => navigate(`/match/${f.id}`)} data-testid={`ipl-fixture-${f.id}`}>
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <span className="text-[10px] sm:text-xs font-semibold uppercase" style={{ color: "var(--text-muted)" }}>Match {f.matchNo}</span>
                <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium flex-shrink-0 ${f.status === 'live' ? 'bg-red-500/10 text-red-500' : f.status === 'completed' ? 'bg-gray-500/10 text-gray-500' : 'bg-blue-500/10 text-blue-500'}`}>
                  {f.status === 'live' ? 'LIVE' : f.status === 'completed' ? 'Completed' : 'Upcoming'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  {f.team1Logo && <img src={f.team1Logo} alt="" className="w-7 h-7 sm:w-10 sm:h-10 object-contain flex-shrink-0" onError={e => e.target.style.display='none'} />}
                  <p className="font-semibold text-xs sm:text-base truncate" style={{ color: "var(--text-primary)" }}>{f.team1Name}</p>
                </div>
                <div className="px-2 sm:px-4 text-center flex-shrink-0">
                  <p className="text-[10px] sm:text-xs mb-0.5 sm:mb-1" style={{ color: "var(--text-muted)" }}>{f.date ? new Date(f.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</p>
                  <p className="text-sm sm:text-lg font-bold" style={{ color: "var(--text-muted)" }}>VS</p>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-1 justify-end min-w-0">
                  <p className="font-semibold text-xs sm:text-base text-right truncate" style={{ color: "var(--text-primary)" }}>{f.team2Name}</p>
                  {f.team2Logo && <img src={f.team2Logo} alt="" className="w-7 h-7 sm:w-10 sm:h-10 object-contain flex-shrink-0" onError={e => e.target.style.display='none'} />}
                </div>
              </div>
              {f.status === 'completed' && f.statusText && (
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3" style={{ borderTop: "1px solid var(--border-color)" }}>
                  <p className="text-xs sm:text-sm font-medium text-[#00bf63] text-center">{f.statusText}</p>
                </div>
              )}
              {f.status !== 'completed' && f.venue && (
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3" style={{ borderTop: "1px solid var(--border-color)" }}>
                  <p className="text-[10px] sm:text-xs text-center truncate" style={{ color: "var(--text-muted)" }}>{f.venue}</p>
                </div>
              )}
            </div>
            {idx === 1 && !isPremium && <InlineAd onUpgradeClick={() => setShowSubscriptionModal(true)} />}
            {idx === 9 && !isPremium && <BannerAd />}
          </React.Fragment>
        ))}
      </div>
    ) : (
      <div className="rounded-2xl shadow-sm overflow-hidden" style={{ background: "var(--bg-card)" }} data-testid="ipl-points-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr style={{ background: "var(--bg-card-inner)", borderBottom: "1px solid var(--border-color)" }}>
              {["#", "Team", "P", "W", "L", "NR", "Pts"].map(h => (
                <th key={h} className={`py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider ${h === "#" || h === "Team" ? "text-left" : "text-center"}`} style={{ color: "var(--text-muted)" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {iplPoints.map((t, i) => (
                <tr key={t.shortName || i} style={{ borderBottom: "1px solid var(--border-color)", background: i < 4 ? "rgba(0,191,99,0.04)" : "transparent" }}>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>{t.position}</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      {t.badge && <img src={t.badge} alt="" className="w-5 h-5 sm:w-6 sm:h-6 object-contain flex-shrink-0" onError={e => e.target.style.display='none'} />}
                      <div><p className="font-semibold text-xs sm:text-sm" style={{ color: "var(--text-primary)" }}>{t.team}</p><p className="text-[10px] sm:text-xs hidden sm:block" style={{ color: "var(--text-muted)" }}>{t.shortName}</p></div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm" style={{ color: "var(--text-secondary)" }}>{t.played}</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm font-semibold text-green-500">{t.won}</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm text-red-500">{t.lost}</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm" style={{ color: "var(--text-muted)" }}>{t.nr}</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm font-bold text-[#1a237e]">{t.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-3" style={{ background: "var(--bg-card-inner)", borderTop: "1px solid var(--border-color)" }}>
          <p className="text-xs text-center" style={{ color: "var(--text-muted)" }}><span className="inline-block w-3 h-3 bg-[#00bf63]/20 rounded mr-1" />Top 4 qualify for playoffs</p>
        </div>
      </div>
    )}
    {!isPremium && <BannerAd />}
  </div>
);

const ComingSoon = ({ sport, message }) => (
  <div className="text-center py-16 rounded-2xl" style={{ background: "var(--bg-card)", border: "1px solid var(--border-color)" }} data-testid="coming-soon">
    <Trophy className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
    <h3 className="text-lg font-semibold mb-1 capitalize" style={{ color: "var(--text-primary)" }}>{sport}</h3>
    <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{message || "Coming soon!"}</p>
  </div>
);

export default Home;
